import { createSelector } from 'reselect';
import { every, filter, flow, get, identity, map, orderBy, values } from 'lodash/fp';

import { pairLists } from '../utils/listUtils';

const orderEntities = ({ isRacing }) =>
  isRacing ? orderBy('gameOrder', 'asc') : orderBy(['score', 'salary'], ['desc', 'desc']);

function makeFilterByStatus(statusFilters = {}) {
  if (every(x => !x, values(statusFilters))) {
    return identity;
  }

  return filter(p => statusFilters[p.statusId]);
}

function getHeadToHead(
  myEntry,
  opponentEntry,
  playerPositions,
  teamPositions,
  statusFilters,
  shareQuota,
  isRacing
) {
  const filterByStatus = makeFilterByStatus(statusFilters);

  const makeList = flow(values, orderEntities({ isRacing }), filterByStatus);

  const players = map(
    pos => ({
      abbr: pos.abbr,
      id: pos.id,
      name: pos.name,
      entities: pairLists(
        makeList(
          get(
            pos.id,
            pos.shareQuota &&
              myEntry.playersSubmitted &&
              myEntry.playersSubmitted[pos.id] &&
              Array.isArray(myEntry.teamsSubmitted) &&
              myEntry.teamsSubmitted.length > 0
              ? {
                  ...myEntry.playersSubmitted,
                  [pos.id]: [...myEntry.playersSubmitted[pos.id], ...myEntry.teamsSubmitted]
                }
              : myEntry.playersSubmitted
          )
        ),
        makeList(
          get(
            pos.id,
            pos.shareQuota &&
              opponentEntry.playersSubmitted &&
              opponentEntry.playersSubmitted[pos.id] &&
              Array.isArray(opponentEntry.teamsSubmitted) &&
              opponentEntry.teamsSubmitted.length > 0
              ? {
                  ...opponentEntry.playersSubmitted,
                  [pos.id]: [
                    ...opponentEntry.playersSubmitted[pos.id],
                    ...opponentEntry.teamsSubmitted
                  ]
                }
              : opponentEntry.playersSubmitted
          )
        ),
        get('id')
      )
    }),
    values(playerPositions)
  );

  const teams = map(
    pos => ({
      abbr: pos.abbr,
      id: pos.id,
      name: pos.name,
      entities: pairLists(
        makeList(myEntry.teamsSubmitted),
        makeList(opponentEntry.teamsSubmitted),
        get('id')
      )
    }),
    values(teamPositions)
  );

  return shareQuota ? players : [...players, ...teams];
}

const headToHeadSelector = createSelector(
  get('entry.myEntry'),
  get('entry.opponentEntry'),
  get('challenge.playerPositions'),
  get('challenge.teamPositions'),
  get('entry.statusFilters'),
  get('challenge.shareQuota'),
  get('challenge.isRacing'),
  getHeadToHead
);

export default headToHeadSelector;
